// Teams Backup — browser entry point.
// Wires up MSAL browser auth, Graph client, browser-fs sink, exporter, and the UI.

import { CONFIG } from './config.js';
import { createBrowserAuth } from '../src/auth/msal-browser.mjs';
import { createGraphClient } from '../src/graph/client.mjs';
import { createBrowserFsSink } from '../src/output/browser-fs.mjs';
import { exportAll } from '../src/exporter.mjs';
import { listChats, chatDisplayName, conversationKind, getMe } from '../src/graph/chats.mjs';

// ---------- feature detection ----------
if (typeof window.showDirectoryPicker !== 'function') {
  document.querySelectorAll('.step').forEach((s) => (s.hidden = true));
  document.querySelector('[data-step="unsupported"]').hidden = false;
  throw new Error('unsupported');
}

// ---------- shared state + helpers ----------
const $ = (id) => document.getElementById(id);
const $$ = (sel) => document.querySelectorAll(sel);
const KINDS = [
  { key: 'chats', label: 'Chats' },
  { key: 'meeting-chats', label: 'Meetings' },
  { key: 'channels', label: 'Channels' },
];
const state = {
  auth: null,
  graph: null,
  me: null,
  chats: [],
  selected: new Set(),
  folderHandle: null,
  outDirName: null,
  sink: null,
  search: '',
  // Which chats to show/back up. When mode === 'skip-preview', no list is
  // shown and the exporter enumerates during backup.
  chatQuery: { since: sinceIsoFromDays(180), until: null },
  mode: 'preview', // 'preview' | 'skip-preview'
};

function sinceIsoFromDays(days) {
  const d = new Date(Date.now() - days * 86400000);
  return d.toISOString();
}

// ---------- persistent folder handle (IndexedDB) ----------
// Store the FileSystemDirectoryHandle keyed by the signed-in user's account id
// so different users on the same browser don't collide. On next visit we can
// re-open the backup without re-picking a folder (browser still requires the
// user to re-grant permission with a click).
const DB_NAME = 'teams-backup';
const DB_STORE = 'handles';

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(DB_STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
async function idbGet(key) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DB_STORE, 'readonly');
    const req = tx.objectStore(DB_STORE).get(key);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
}
async function idbPut(key, value) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DB_STORE, 'readwrite');
    tx.objectStore(DB_STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
async function idbDelete(key) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DB_STORE, 'readwrite');
    tx.objectStore(DB_STORE).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

function handleKeyFor(account) {
  const oid = account?.homeAccountId || account?.localAccountId || account?.username || 'anon';
  return `handle::${oid}`;
}
function favoritesKeyFor(account) {
  const oid = account?.homeAccountId || account?.localAccountId || account?.username || 'anon';
  return `favorites::${oid}`;
}

async function loadFavorites() {
  const account = await state.auth.whoAmI().catch(() => null);
  if (!account) return new Set();
  const arr = await idbGet(favoritesKeyFor(account)).catch(() => null);
  return new Set(Array.isArray(arr) ? arr : []);
}
async function saveFavorites(set) {
  const account = await state.auth.whoAmI().catch(() => null);
  if (!account) return;
  await idbPut(favoritesKeyFor(account), [...set]).catch(() => null);
}

async function saveHandleForUser(handle, displayName) {
  const account = await state.auth.whoAmI().catch(() => null);
  if (!account) return;
  await idbPut(handleKeyFor(account), {
    handle,
    displayName: displayName || handle.name,
    savedAt: new Date().toISOString(),
  }).catch(() => null);
}

async function loadHandleForUser() {
  const account = await state.auth.whoAmI().catch(() => null);
  if (!account) return null;
  return await idbGet(handleKeyFor(account)).catch(() => null);
}

async function forgetHandleForUser() {
  const account = await state.auth.whoAmI().catch(() => null);
  if (!account) return;
  await idbDelete(handleKeyFor(account)).catch(() => null);
}

// Verify we can still write to the saved handle; if the OS folder is gone or
// permission was revoked, returns false and we fall back to a fresh picker.
async function verifyHandlePermission(handle, { prompt = false } = {}) {
  if (!handle) return false;
  try {
    const opts = { mode: 'readwrite' };
    if ((await handle.queryPermission(opts)) === 'granted') return true;
    if (!prompt) return false;
    return (await handle.requestPermission(opts)) === 'granted';
  } catch {
    return false;
  }
}
const browseState = {
  rootHandle: null,
  manifest: null,
  active: null,
  search: '',
  favorites: new Set(),
  filterMode: 'all', // 'all' | 'starred'
};

const escapeHtml = (s) => String(s || '').replace(/[&<>"']/g, (c) => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' })[c]);
const initials = (n) => (String(n || '').trim().split(/\s+/).slice(0, 2).map((p) => p[0] || '').join('') || '?').toUpperCase();

function setStep(name) {
  $$('.step').forEach((s) => (s.hidden = s.dataset.step !== name));
  const order = ['signin', 'pick', 'progress', 'done'];
  const activeIdx =
    name === 'failed' ? 2 :
    name === 'browse' ? 4 :
    order.indexOf(name);
  for (const dot of $$('.step-dot')) {
    const i = order.indexOf(dot.dataset.step);
    dot.classList.remove('active', 'done');
    if (i < activeIdx) dot.classList.add('done');
    else if (i === activeIdx) dot.classList.add('active');
  }
}

function setAccount(account) {
  if (account) {
    $('account').innerHTML = `Signed in as <strong>${escapeHtml(account.username || account.name || '')}</strong>`;
  } else {
    $('account').textContent = '';
  }
}

function shortDate(iso) {
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (d.getFullYear() === now.getFullYear()) return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  return d.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' });
}

// ---------- auth ----------
state.auth = createBrowserAuth({
  clientId: CONFIG.clientId,
  tenantId: CONFIG.tenantId,
  scopes: CONFIG.scopes,
  onInteractiveRequired: async ({ reason }) => {
    // Called just before MSAL pops up a sign-in window. If we don't have a
    // user gesture attached, the popup will be blocked. Show a banner asking
    // the user to click a button so we get one.
    if (reason === 'expired') {
      await promptReauth();
    }
  },
});
state.graph = createGraphClient({ auth: state.auth });

function promptReauth() {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'reauth-overlay';
    overlay.innerHTML = `
      <div class="reauth-card">
        <h2>Reconnect to Microsoft</h2>
        <p>Your sign-in has expired. Click below to reopen the Microsoft popup — the backup will pick up where it left off.</p>
        <button class="btn primary big" id="reauth-btn">Reconnect</button>
      </div>`;
    document.body.appendChild(overlay);
    overlay.querySelector('#reauth-btn').addEventListener('click', () => {
      overlay.remove();
      resolve();
    });
  });
}

$('btn-signin').addEventListener('click', async () => {
  const btn = $('btn-signin');
  btn.disabled = true;
  btn.textContent = 'Signing in…';
  try {
    await state.auth.signIn();
    const account = await state.auth.whoAmI();
    setAccount(account ? { username: account.username, name: account.name } : null);
    await routePostSignIn();
  } catch (err) {
    alert(`Sign-in failed: ${err.message || err}`);
    btn.disabled = false;
    btn.textContent = 'Sign in with Microsoft';
  }
});

// After sign-in, check if this user has a previously-saved backup folder
// handle on this browser. If yes, offer to reopen it; otherwise go to pick.
async function routePostSignIn() {
  const rec = await loadHandleForUser();
  if (rec?.handle) {
    $('landing-path').textContent = rec.displayName || rec.handle.name;
    state.savedRecord = rec;
    setStep('landing');
    return;
  }
  await goToPick();
}

$('btn-open-existing').addEventListener('click', async () => {
  const rec = state.savedRecord;
  if (!rec?.handle) return goToPick();
  const ok = await verifyHandlePermission(rec.handle, { prompt: true });
  if (!ok) {
    alert("Couldn't re-open that folder — it may have been moved or permission was denied. Starting a new backup.");
    await forgetHandleForUser();
    return goToPick();
  }
  state.folderHandle = rec.handle;
  state.outDirName = rec.displayName || rec.handle.name;
  await enterBrowse();
});

$('btn-start-new').addEventListener('click', () => goToPick());
$('link-pick-other').addEventListener('click', (e) => { e.preventDefault(); pickExistingBackupFolder(); });
$('link-pick-existing').addEventListener('click', (e) => { e.preventDefault(); pickExistingBackupFolder(); });

// Walk into `dir` looking for a folder that has a data/ subdirectory. If it
// also has data/manifest.json we're set; otherwise we treat it as a partial
// (interrupted-first-run) backup and synthesize the manifest from disk.
async function findBackupDir(dir, remaining, pathParts) {
  try {
    const dataDir = await dir.getDirectoryHandle('data');
    // Prefer manifest if it exists.
    try {
      await dataDir.getFileHandle('manifest.json');
      return { dir, pathParts, hasManifest: true };
    } catch { /* no manifest — check if at least one conversation folder exists */ }
    for await (const child of dataDir.values()) {
      if (child.kind === 'directory') return { dir, pathParts, hasManifest: false };
    }
  } catch { /* no data dir here — recurse */ }
  if (remaining <= 0) return null;
  try {
    for await (const child of dir.values()) {
      if (child.kind !== 'directory') continue;
      const found = await findBackupDir(child, remaining - 1, [...pathParts, child.name]);
      if (found) return found;
    }
  } catch { /* no permission to iterate — bail */ }
  return null;
}

// Reconstruct a manifest by walking data/*/*/messages.json (and channels one
// level deeper). Used when the manifest is missing or when the Rebuild-index
// button is clicked on a partial state. Returns { generatedAt: null,
// conversations: [...] } so the caller can persist it and downstream code
// treats null generatedAt as "partial — skip server-side chat filter".
async function rebuildManifestFromDisk(rootHandle) {
  const entries = [];
  const dataDir = await rootHandle.getDirectoryHandle('data').catch(() => null);
  if (!dataDir) return { generatedAt: null, conversations: [] };

  async function readEntry(dir, kind, relDir) {
    try {
      const fh = await dir.getFileHandle('messages.json');
      const file = await fh.getFile();
      const payload = JSON.parse(await file.text());
      const msgs = payload.messages || [];
      return {
        id: payload.id,
        kind,
        name: payload.name || dir.name,
        slug: dir.name,
        relDir,
        conversationJs: `${relDir}/conversation.js`,
        messagesJson: `${relDir}/messages.json`,
        messageCount: msgs.length,
        attachmentCount: msgs.reduce((n, m) => n + (m.attachments?.filter((a) => a.localPath).length || 0), 0),
        recordingCount: msgs.reduce((n, m) => n + (m.event?.kind === 'recording' && m.event?.localPath ? 1 : 0), 0),
        transcriptCount: msgs.reduce((n, m) => n + (m.event?.transcriptLocalPath ? 1 : 0), 0),
        lastMessageAt: msgs.length ? (msgs[msgs.length - 1].timestamp || null) : null,
      };
    } catch { return null; }
  }

  for (const kind of ['chats', 'meeting-chats']) {
    const kd = await dataDir.getDirectoryHandle(kind).catch(() => null);
    if (!kd) continue;
    for await (const conv of kd.values()) {
      if (conv.kind !== 'directory') continue;
      const e = await readEntry(conv, kind, `data/${kind}/${conv.name}`);
      if (e) entries.push(e);
    }
  }
  const chDir = await dataDir.getDirectoryHandle('channels').catch(() => null);
  if (chDir) {
    for await (const team of chDir.values()) {
      if (team.kind !== 'directory') continue;
      for await (const chan of team.values()) {
        if (chan.kind !== 'directory') continue;
        const e = await readEntry(chan, 'channels', `data/channels/${team.name}/${chan.name}`);
        if (e) entries.push(e);
      }
    }
  }

  entries.sort((a, b) => (b.lastMessageAt || '').localeCompare(a.lastMessageAt || ''));
  return { generatedAt: null, conversations: entries };
}

// Central place to load the manifest; if it's missing (interrupted first
// run) we auto-rebuild from what's on disk and persist so future opens are
// clean. Called by both pickExistingBackupFolder and enterBrowse.
async function loadOrRebuildManifest(rootHandle) {
  try {
    const dataDir = await rootHandle.getDirectoryHandle('data');
    const fh = await dataDir.getFileHandle('manifest.json');
    const file = await fh.getFile();
    return JSON.parse(await file.text());
  } catch {
    const rebuilt = await rebuildManifestFromDisk(rootHandle);
    if (rebuilt.conversations.length) {
      try {
        const sink = createBrowserFsSink({ rootHandle });
        await sink.init();
        await sink.finalizeManifest(rebuilt.conversations, { generatedAt: null });
      } catch { /* write failed — user can still browse in-memory */ }
    }
    return rebuilt;
  }
}

async function pickExistingBackupFolder() {
  try {
    const parent = await window.showDirectoryPicker({ mode: 'readwrite', id: 'teams-backup-open', startIn: 'documents' });
    const found = await findBackupDir(parent, 2, []);
    if (!found) {
      alert("That folder doesn't look like a Teams Backup — I searched 2 levels deep and couldn't find a data/ folder inside it.");
      return;
    }
    const displayName = found.pathParts.length
      ? `${parent.name}/${found.pathParts.join('/')}`
      : parent.name;
    state.folderHandle = found.dir;
    state.outDirName = displayName;
    await saveHandleForUser(found.dir, displayName);
    if (!found.hasManifest) {
      // Interrupted first-run recovery: rebuilt manifest gets persisted with
      // generatedAt=null so Update backup enumerates everything.
      await enterBrowse();  // enterBrowse calls loadOrRebuildManifest
    } else {
      await enterBrowse();
    }
  } catch (err) {
    if (err.name !== 'AbortError') alert(`Couldn't open folder: ${err.message}`);
  }
}

// ---------- pick ----------
// Reload identity + chat list based on state.chatQuery. Called on entering the
// pick step and whenever the range control changes.
let currentLoad = 0;
async function goToPick() {
  setStep('pick');
  applyModeUI();
  // Show the incremental toggle only if we have an existing manifest to
  // build on top of. Otherwise stay hidden and default to fresh.
  await maybeLoadExistingManifest();
  const me = await getMe(state.graph).catch(() => null);
  state.me = me;
  if (me) setAccount({ username: me.userPrincipalName, name: me.displayName });
  if (state.mode === 'skip-preview') {
    updateSummary();
    return;
  }
  await reloadChatList();
}

async function maybeLoadExistingManifest() {
  state.existingManifest = null;
  state.backupMode = 'fresh';
  $('mode-toggle').hidden = true;
  const rec = await loadHandleForUser();
  if (!rec?.handle) return;
  const ok = await verifyHandlePermission(rec.handle);
  if (!ok) return; // silent — user hasn't re-granted; incremental just won't offer
  try {
    const dataDir = await rec.handle.getDirectoryHandle('data');
    const fh = await dataDir.getFileHandle('manifest.json');
    const file = await fh.getFile();
    state.existingManifest = JSON.parse(await file.text());
    state.existingHandle = rec.handle;
    state.existingDisplayName = rec.displayName || rec.handle.name;
    $('mode-toggle').hidden = false;
  } catch { /* no valid manifest — skip */ }
}

document.querySelectorAll('input[name="backup-mode"]').forEach((el) => {
  el.addEventListener('change', (e) => { state.backupMode = e.target.value; updateSummary(); });
});

async function reloadChatList() {
  const loadId = ++currentLoad;
  state.chats = [];
  state.selected = new Set();
  $('pick-list').innerHTML = `
    <div class="loading" id="pick-loading">
      <div class="spinner"></div>
      <span id="pick-loading-status">Loading your chats…</span>
    </div>`;
  updateSummary();

  try {
    const meId = state.me?.id || null;
    let renderPending = false;
    const scheduleRender = () => {
      if (renderPending) return;
      renderPending = true;
      setTimeout(() => {
        renderPending = false;
        if (loadId !== currentLoad) return;
        renderChatList();
        updateSummary();
      }, 300);
    };

    for await (const c of listChats(state.graph, state.chatQuery)) {
      if (loadId !== currentLoad) return; // superseded by a newer range change
      state.chats.push({
        id: c.id,
        name: chatDisplayName(c, meId),
        kind: conversationKind(c),
        chatType: c.chatType,
        memberCount: (c.members || []).length,
        lastUpdated: c.lastUpdatedDateTime || c.createdDateTime || null,
        raw: c,
      });
      state.selected.add(c.id);
      const status = $('pick-loading-status');
      if (status) status.textContent = `Loading… ${state.chats.length} so far`;
      scheduleRender();
    }

    if (loadId !== currentLoad) return;
    state.chats.sort((a, b) => (b.lastUpdated || '').localeCompare(a.lastUpdated || ''));
    renderChatList();
    updateSummary();
  } catch (err) {
    if (loadId !== currentLoad) return;
    $('pick-list').innerHTML = `<div class="loading" style="color:var(--danger)">Failed to load chats: ${escapeHtml(err.message)}</div>`;
  }
}

function applyModeUI() {
  const skip = state.mode === 'skip-preview';
  $('chat-list-wrap').hidden = skip;
  $('skip-preview-shell').hidden = !skip;
  $('btn-select-all').hidden = skip;
  $('btn-select-none').hidden = skip;
  $('pick-search').hidden = skip;
  $('custom-range-row').hidden = state.mode !== 'custom';
}

$('pick-range').addEventListener('change', (e) => {
  const v = e.target.value;
  if (v === 'all-skip') {
    state.mode = 'skip-preview';
    state.chatQuery = {};
    applyModeUI();
    updateSummary();
    return;
  }
  if (v === 'custom') {
    state.mode = 'custom';
    applyModeUI();
    // Wait for user to click Apply.
    return;
  }
  const days = Number(v);
  state.mode = 'preview';
  state.chatQuery = { since: sinceIsoFromDays(days), until: null };
  applyModeUI();
  reloadChatList();
});

$('btn-apply-range').addEventListener('click', () => {
  const from = $('range-from').value;
  const to = $('range-to').value;
  state.chatQuery = {
    since: from ? new Date(from).toISOString() : null,
    until: to ? new Date(`${to}T23:59:59Z`).toISOString() : null,
  };
  reloadChatList();
});

function renderChatList() {
  const list = $('pick-list');
  const loading = $('pick-loading');
  list.innerHTML = '';
  // Sort by most-recently-active on every render so streaming inserts land
  // in the right position.
  const sorted = [...state.chats].sort((a, b) => (b.lastUpdated || '').localeCompare(a.lastUpdated || ''));
  const q = state.search.trim().toLowerCase();
  for (const { key, label } of KINDS) {
    const items = sorted
      .filter((c) => c.kind === key)
      .filter((c) => !q || (c.name || '').toLowerCase().includes(q));
    if (!items.length) continue;
    const head = document.createElement('div');
    head.className = 'section-head';
    head.innerHTML = `<span>${label}</span><span class="count"><span data-selected>0</span> / ${items.length} selected</span>`;
    list.appendChild(head);
    for (const chat of items) {
      const row = document.createElement('label');
      row.className = 'chat-row';
      if (state.selected.has(chat.id)) row.classList.add('selected');
      const cb = document.createElement('input');
      cb.type = 'checkbox';
      cb.checked = state.selected.has(chat.id);
      cb.addEventListener('change', () => {
        if (cb.checked) state.selected.add(chat.id);
        else state.selected.delete(chat.id);
        row.classList.toggle('selected', cb.checked);
        updateSectionCount(head, items);
        updateSummary();
      });
      const av = document.createElement('div'); av.className = 'avatar'; av.textContent = initials(chat.name);
      const info = document.createElement('div'); info.className = 'chat-info';
      const n = document.createElement('div'); n.className = 'chat-name'; n.textContent = chat.name;
      const m = document.createElement('div'); m.className = 'chat-meta';
      const bits = [];
      if (chat.memberCount) bits.push(`${chat.memberCount} member${chat.memberCount === 1 ? '' : 's'}`);
      if (chat.lastUpdated) bits.push(`last active ${shortDate(chat.lastUpdated)}`);
      m.textContent = bits.join(' · ');
      info.append(n, m);
      row.append(cb, av, info);
      list.appendChild(row);
    }
    updateSectionCount(head, items);
  }
  if (!list.children.length) list.innerHTML = `<div class="loading">No conversations match your filter.</div>`;
  // Keep the spinner visible at the bottom while pagination is still in progress.
  if (loading) list.appendChild(loading);
}
function updateSectionCount(head, items) {
  const selected = items.filter((c) => state.selected.has(c.id)).length;
  const el = head.querySelector('[data-selected]');
  if (el) el.textContent = String(selected);
}
function updateSummary() {
  if (state.mode === 'skip-preview') {
    $('pick-summary').textContent = 'All chats will be backed up (enumerated as we go).';
    $('btn-start').disabled = !state.folderHandle;
    return;
  }
  const n = state.selected.size;
  $('pick-summary').textContent = `${n} conversation${n === 1 ? '' : 's'} selected`;
  $('btn-start').disabled = !state.folderHandle || n === 0;
}

$('pick-search').addEventListener('input', (e) => { state.search = e.target.value; renderChatList(); });
$('btn-select-all').addEventListener('click', () => { state.selected = new Set(state.chats.map((c) => c.id)); renderChatList(); updateSummary(); });
$('btn-select-none').addEventListener('click', () => { state.selected.clear(); renderChatList(); updateSummary(); });

$('btn-choose-folder').addEventListener('click', async () => {
  try {
    const handle = await window.showDirectoryPicker({ mode: 'readwrite', id: 'teams-backup', startIn: 'documents' });
    const subdir = await handle.getDirectoryHandle('teams-backup', { create: true });
    state.folderHandle = subdir;
    state.outDirName = `${handle.name}/teams-backup`;
    $('folder-path').textContent = state.outDirName;
    $('folder-path').classList.remove('muted');
    $('folder-path').classList.add('set');
    updateSummary();
    // Persist so revisits can offer "Browse existing".
    await saveHandleForUser(subdir, state.outDirName);
  } catch (err) {
    if (err.name !== 'AbortError') alert(`Folder picker failed: ${err.message}`);
  }
});

$('btn-start').addEventListener('click', async () => {
  const skipPreview = state.mode === 'skip-preview';
  const incremental = state.backupMode === 'incremental' && state.existingManifest;

  // Incremental writes back into the existing folder. If the user hasn't
  // picked a new folder, use the saved handle. Ask for re-permission (needs
  // to be tied to this click gesture).
  if (incremental && !state.folderHandle) {
    const ok = await verifyHandlePermission(state.existingHandle, { prompt: true });
    if (!ok) { alert("Can't reopen the saved folder — pick one manually."); return; }
    state.folderHandle = state.existingHandle;
    state.outDirName = state.existingDisplayName;
  }

  const selectedIds = skipPreview ? null : new Set(state.selected);
  const initialTotal = skipPreview ? '?' : selectedIds.size;
  setStep('progress');
  $('progress-count').textContent = `0 / ${initialTotal}`;
  $('progress-percent').textContent = skipPreview ? '' : '0%';
  $('progress-current').textContent = skipPreview
    ? 'Enumerating your chats…'
    : (incremental ? 'Checking for new messages…' : 'Starting up…');
  $('progress-fill').style.width = '0%';
  $('log').innerHTML = '';
  resetLogs();
  resetStopButton();

  const sink = createBrowserFsSink({ rootHandle: state.folderHandle });
  state.sink = sink;
  state.abortController = new AbortController();

  const logger = {
    info: (m) => appendLog('info', m),
    warn: (m) => appendLog('warn', m),
    error: (m) => appendLog('error', m),
  };
  const filter = selectedIds ? (chat) => selectedIds.has(chat.id) : null;

  try {
    await exportAll({
      signal: state.abortController.signal,
      graph: state.graph,
      sink,
      options: {
        downloadAttachments: $('opt-attachments').checked,
        downloadInlineImages: true,
        downloadRecordings: $('opt-recordings').checked,
        downloadTranscripts: $('opt-transcripts').checked,
        includeChannels: false,
        concurrency: 6,
      },
      logger,
      filter,
      chatQuery: skipPreview ? {} : state.chatQuery,
      existingManifest: incremental ? state.existingManifest : null,
      onProgress: ({ done, total, current }) => updateProgress(done, total, current),
    });
    await persistLogs(sink, incremental ? 'update' : 'backup');
    $('done-summary').textContent = `Saved to ${state.outDirName}`;
    setStep('done');
  } catch (err) {
    $('failed-message').textContent = err.message || String(err);
    appendLog('error', err.stack || err.message);
    await persistLogs(sink, 'failed');
    setStep('failed');
  }
});

function updateProgress(done, total, current) {
  const pct = total ? Math.round((done / total) * 100) : 0;
  $('progress-fill').style.width = `${pct}%`;
  $('progress-count').textContent = `${done} / ${total}`;
  $('progress-percent').textContent = `${pct}%`;
  if (current) $('progress-current').textContent = current;
}
// Log lines are captured to a module-level buffer as well as rendered, so
// Copy Logs / auto-save can pull the full stream at any time.
let currentRunLogs = [];
function resetLogs() { currentRunLogs = []; }
function appendLog(level, message) {
  const stamp = new Date().toISOString();
  currentRunLogs.push(`[${stamp}] [${(level || 'info').toUpperCase()}] ${message}`);
  const log = $('log');
  const line = document.createElement('div');
  line.className = level || 'info';
  line.textContent = message;
  log.appendChild(line);
  log.scrollTop = log.scrollHeight;
}

function resetStopButton() {
  const btn = $('btn-stop-backup');
  btn.disabled = false;
  btn.textContent = '⏹ Stop';
}

$('btn-stop-backup').addEventListener('click', () => {
  if (state.abortController && !state.abortController.signal.aborted) {
    appendLog('warn', 'Stop requested — finishing current chats and wrapping up…');
    state.abortController.abort();
    $('btn-stop-backup').disabled = true;
    $('btn-stop-backup').textContent = 'Stopping…';
  }
});

$('log-copy').addEventListener('click', async () => {
  const btn = $('log-copy');
  const original = btn.textContent;
  try {
    await navigator.clipboard.writeText(currentRunLogs.join('\n'));
    btn.textContent = 'Copied ✓';
  } catch {
    btn.textContent = 'Failed';
  }
  setTimeout(() => { btn.textContent = original; }, 1500);
});

// Best-effort: writes the current log buffer to <backup>/logs/run-<ts>.log
// on the picked folder. Silently no-ops if the sink isn't ready or permission
// was revoked.
async function persistLogs(sink, prefix = 'run') {
  if (!sink || !currentRunLogs.length) return;
  try {
    const rel = await sink.writeLog(currentRunLogs.join('\n'), { prefix });
    appendLog('info', `Saved log to ${rel}`);
  } catch (err) {
    // don't crash the flow just because we couldn't write a log
    console.warn('log persist failed', err);
  }
}
$('log-toggle').addEventListener('click', () => {
  const log = $('log');
  const btn = $('log-toggle');
  log.classList.toggle('collapsed');
  btn.textContent = log.classList.contains('collapsed') ? 'Show' : 'Hide';
});

// ---------- done ----------
$('btn-browse').addEventListener('click', () => enterBrowse());
$('btn-new-backup-done').addEventListener('click', () => setStep('pick'));
$('btn-retry').addEventListener('click', () => setStep('pick'));

// ---------- browse ----------
async function enterBrowse() {
  setStep('browse');
  browseState.rootHandle = state.folderHandle;
  $('browse-subtitle').textContent = state.outDirName || '';
  $('browse-conv-list').innerHTML = `<div class="loading"><div class="spinner"></div><span>Loading manifest…</span></div>`;
  $('browse-welcome').hidden = false;
  $('browse-conv').hidden = true;
  browseState.favorites = await loadFavorites();
  try {
    browseState.manifest = await loadOrRebuildManifest(browseState.rootHandle);
    updatePartialBanner();
    renderBrowseSidebar();
  } catch (err) {
    $('browse-conv-list').innerHTML = `<div class="loading" style="color:var(--danger)">Couldn't open backup: ${escapeHtml(err.message)}</div>`;
  }
}

function updatePartialBanner() {
  const el = $('partial-banner');
  if (!el) return;
  const isPartial = browseState.manifest && browseState.manifest.generatedAt === null && (browseState.manifest.conversations?.length || 0) > 0;
  el.hidden = !isPartial;
}

document.querySelectorAll('.filter-tab').forEach((tab) => {
  tab.addEventListener('click', () => {
    browseState.filterMode = tab.dataset.filter;
    document.querySelectorAll('.filter-tab').forEach((t) => t.classList.toggle('active', t === tab));
    renderBrowseSidebar();
  });
});

$('btn-new-backup').addEventListener('click', () => setStep('pick'));

$('btn-reindex').addEventListener('click', async () => {
  if (!browseState.rootHandle || !browseState.manifest) return;
  const ok = await verifyHandlePermission(browseState.rootHandle, { prompt: true });
  if (!ok) { alert("Can't write to this folder — permission denied."); return; }
  const btn = $('btn-reindex');
  const original = btn.textContent;
  btn.disabled = true;
  btn.textContent = 'Rebuilding…';
  try {
    const sink = createBrowserFsSink({ rootHandle: browseState.rootHandle });
    const rebuilt = [];
    let touched = 0, changed = 0;
    for (const entry of browseState.manifest.conversations || []) {
      touched += 1;
      try {
        const payload = await sink.readMessagesJson(entry.relDir);
        const msgs = payload.messages || [];
        // messages.json is written oldest → newest; last one is the newest.
        const newestTs = msgs.length ? (msgs[msgs.length - 1].timestamp || null) : null;
        const attachmentCount = msgs.reduce((n, m) => n + (m.attachments?.filter((a) => a.localPath).length || 0), 0);
        const recordingCount = msgs.reduce((n, m) => n + (m.event?.kind === 'recording' && m.event?.localPath ? 1 : 0), 0);
        const transcriptCount = msgs.reduce((n, m) => n + (m.event?.transcriptLocalPath ? 1 : 0), 0);
        const next = {
          ...entry,
          messageCount: msgs.length,
          lastMessageAt: newestTs || entry.lastMessageAt || null,
          attachmentCount,
          recordingCount,
          transcriptCount,
        };
        if (JSON.stringify(next) !== JSON.stringify(entry)) changed += 1;
        rebuilt.push(next);
      } catch {
        rebuilt.push(entry); // couldn't read — keep the old entry
      }
    }
    rebuilt.sort((a, b) => (b.lastMessageAt || '').localeCompare(a.lastMessageAt || ''));
    await sink.finalizeManifest(rebuilt);
    browseState.manifest = { generatedAt: new Date().toISOString(), conversations: rebuilt };
    renderBrowseSidebar();
    btn.textContent = `Rebuilt (${changed} of ${touched} updated)`;
  } catch (err) {
    btn.textContent = 'Failed';
    alert(`Rebuild failed: ${err.message}`);
  } finally {
    setTimeout(() => { btn.disabled = false; btn.textContent = original; }, 2500);
  }
});

$('btn-update-backup').addEventListener('click', async () => {
  // Kick off an incremental run against the currently-open backup.
  // No chat picker: back up everything, per-chat only pull new messages.
  if (!browseState.rootHandle || !browseState.manifest) return;
  const ok = await verifyHandlePermission(browseState.rootHandle, { prompt: true });
  if (!ok) { alert("Can't write to this folder — permission denied."); return; }

  // Set up the export state to mirror what pick+start would have configured.
  state.folderHandle = browseState.rootHandle;
  state.outDirName = state.outDirName || browseState.rootHandle.name;
  state.existingManifest = browseState.manifest;

  // Progress UI reset.
  setStep('progress');
  $('progress-count').textContent = `0 / ?`;
  $('progress-percent').textContent = '';
  $('progress-current').textContent = 'Looking for new messages…';
  $('progress-fill').style.width = '0%';
  $('log').innerHTML = '';

  const sink = createBrowserFsSink({ rootHandle: state.folderHandle });
  const logger = {
    info: (m) => appendLog('info', m),
    warn: (m) => appendLog('warn', m),
    error: (m) => appendLog('error', m),
  };

  // Only enumerate chats that showed activity since ~last backup. We back
  // the cutoff off by 5 minutes to guard against any propagation lag between
  // a message arriving and `lastUpdatedDateTime` bumping.
  const generatedAt = browseState.manifest.generatedAt;
  const bufferedSince = generatedAt
    ? new Date(new Date(generatedAt).getTime() - 5 * 60 * 1000).toISOString()
    : null;

  resetLogs();
  resetStopButton();
  state.abortController = new AbortController();
  try {
    await exportAll({
      signal: state.abortController.signal,
      graph: state.graph,
      sink,
      options: {
        downloadAttachments: true,
        downloadInlineImages: true,
        downloadRecordings: true,
        downloadTranscripts: true,
        includeChannels: false,
        concurrency: 6,
      },
      logger,
      filter: null,
      chatQuery: bufferedSince ? { since: bufferedSince } : {},
      existingManifest: browseState.manifest,
      onProgress: ({ done, total, current }) => updateProgress(done, total, current),
    });
    await persistLogs(sink, 'update');
    appendLog('info', 'Reloading updated manifest…');
    await enterBrowse();
  } catch (err) {
    $('failed-message').textContent = err.message || String(err);
    appendLog('error', err.stack || err.message);
    await persistLogs(sink, 'update-failed');
    setStep('failed');
  }
});
$('browse-search').addEventListener('input', (e) => { browseState.search = e.target.value; renderBrowseSidebar(); });

function renderBrowseSidebar() {
  const list = $('browse-conv-list');
  list.innerHTML = '';
  const q = browseState.search.trim().toLowerCase();
  // Sort here regardless of manifest order so the newest conversation always
  // floats to the top after an update, even if the on-disk manifest wasn't
  // re-sorted for any reason.
  const convs = (browseState.manifest?.conversations || [])
    .filter((c) => !q || (c.name || '').toLowerCase().includes(q))
    .filter((c) => browseState.filterMode !== 'starred' || browseState.favorites.has(c.id))
    .sort((a, b) => (b.lastMessageAt || '').localeCompare(a.lastMessageAt || ''));
  let any = false;
  for (const { key, label } of KINDS) {
    const items = convs.filter((c) => c.kind === key);
    if (!items.length) continue;
    any = true;
    const head = document.createElement('div');
    head.className = 'section-head';
    head.innerHTML = `<span>${label}</span><span class="count">${items.length}</span>`;
    list.appendChild(head);
    for (const conv of items) {
      const row = document.createElement('div');
      row.className = 'browse-conv-row' + (browseState.active?.id === conv.id ? ' active' : '');
      const starred = browseState.favorites.has(conv.id);
      row.innerHTML = `
        <button class="star-btn" title="${starred ? 'Unstar' : 'Star'} this chat">${starred ? '★' : '☆'}</button>
        <div class="avatar"></div>
        <div class="info"><div class="name"></div><div class="meta"></div></div>`;
      row.querySelector('.avatar').textContent = initials(conv.name);
      row.querySelector('.name').textContent = conv.name || '(unnamed)';
      const bits = [];
      if (conv.messageCount != null) bits.push(`${conv.messageCount} msgs`);
      if (conv.attachmentCount) bits.push(`📎 ${conv.attachmentCount}`);
      if (conv.recordingCount) bits.push(`🎥 ${conv.recordingCount}`);
      row.querySelector('.meta').textContent = bits.join(' · ');
      const starBtn = row.querySelector('.star-btn');
      if (starred) starBtn.classList.add('active');
      starBtn.addEventListener('click', async (e) => {
        e.stopPropagation();
        if (browseState.favorites.has(conv.id)) browseState.favorites.delete(conv.id);
        else browseState.favorites.add(conv.id);
        await saveFavorites(browseState.favorites);
        renderBrowseSidebar();
      });
      row.addEventListener('click', () => openBrowseConv(conv));
      list.appendChild(row);
    }
  }
  if (!any) {
    list.innerHTML = browseState.filterMode === 'starred'
      ? `<div class="loading">No starred conversations yet. Click ☆ next to any chat to star it.</div>`
      : `<div class="loading">No conversations match.</div>`;
  }
}

async function openBrowseConv(conv) {
  browseState.active = conv;
  renderBrowseSidebar();
  $('browse-welcome').hidden = true;
  $('browse-conv').hidden = false;
  $('browse-conv-title').textContent = conv.name || '(unnamed)';
  $('browse-conv-sub').textContent = 'Loading…';
  $('browse-messages').innerHTML = `<div class="loading"><div class="spinner"></div><span>Loading conversation…</span></div>`;
  $('msg-search').value = '';
  try {
    // Walk the relDir path from root
    const parts = conv.relDir.split('/').filter(Boolean);
    let dir = browseState.rootHandle;
    for (const p of parts) dir = await dir.getDirectoryHandle(p);
    const file = await (await dir.getFileHandle('messages.json')).getFile();
    const payload = JSON.parse(await file.text());
    await renderBrowseConv(conv, payload, dir);
  } catch (err) {
    $('browse-messages').innerHTML = `<div class="loading" style="color:var(--danger)">Failed: ${escapeHtml(err.message)}</div>`;
  }
}

async function renderBrowseConv(conv, payload, convDir) {
  const members = (payload.members || []).map((m) => m.displayName).filter(Boolean);
  const bits = [];
  if (payload.chatType) bits.push(payload.chatType);
  if (members.length) bits.push(members.join(', '));
  bits.push(`${payload.messageCount} messages`);
  $('browse-conv-sub').textContent = bits.join(' · ');

  // Preload attachment blob URLs on demand — inline images need src=blob:
  // For simplicity, build a map of attachment filename → blob URL only when clicked/rendered.
  // For inline images (hosted content rewritten to relative paths), we resolve those upfront.
  let attachmentsDir = null;
  try { attachmentsDir = await convDir.getDirectoryHandle('attachments'); } catch { /* none */ }
  const attachmentBlobUrls = new Map(); // filename → blob URL

  async function getAttachmentUrl(relPath) {
    const filename = relPath.split('/').pop();
    if (attachmentBlobUrls.has(filename)) return attachmentBlobUrls.get(filename);
    if (!attachmentsDir) return null;
    try {
      const fh = await attachmentsDir.getFileHandle(filename);
      const file = await fh.getFile();
      const url = URL.createObjectURL(file);
      attachmentBlobUrls.set(filename, url);
      return url;
    } catch { return null; }
  }

  // Pre-resolve inline images so they render immediately
  const inlineNeeded = new Set();
  for (const msg of payload.messages || []) {
    const html = msg.body?.content || '';
    for (const m of html.matchAll(/(src|href)="attachments\/([^"]+)"/gi)) inlineNeeded.add(m[2]);
  }
  await Promise.all([...inlineNeeded].map((n) => getAttachmentUrl(`attachments/${n}`)));

  const container = $('browse-messages');
  container.innerHTML = '';
  let currentDay = null;
  let lastAuthor = null;
  let lastGroup = null;

  for (const msg of payload.messages || []) {
    const d = msg.timestamp ? new Date(msg.timestamp) : null;
    if (d) {
      const key = d.toDateString();
      if (key !== currentDay) {
        const sep = document.createElement('div');
        sep.className = 'day-sep';
        sep.textContent = d.toLocaleDateString([], { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
        container.appendChild(sep);
        currentDay = key;
        lastAuthor = null;
      }
    }
    const isSystem = msg.messageType === 'systemEventMessage' || msg.author?.kind === 'system';
    const authorKey = msg.author?.id || msg.author?.name;
    const sameAuthor = !isSystem && lastAuthor === authorKey;

    if (!sameAuthor) {
      lastGroup = document.createElement('div');
      lastGroup.className = 'msg-group' + (isSystem ? ' system' : '');
      const av = document.createElement('div'); av.className = 'avatar'; av.textContent = initials(msg.author?.name || '?');
      const body = document.createElement('div'); body.className = 'msg-body';
      const head = document.createElement('div'); head.className = 'msg-head';
      const auth = document.createElement('span'); auth.className = 'msg-author'; auth.textContent = msg.author?.name || 'Unknown';
      const time = document.createElement('span'); time.className = 'msg-time'; time.textContent = d ? d.toLocaleString() : '';
      head.append(auth, time);
      body.appendChild(head);
      lastGroup.append(av, body);
      container.appendChild(lastGroup);
      lastAuthor = authorKey;
    }
    const target = lastGroup.querySelector('.msg-body');
    target.appendChild(await renderBrowseMessage(msg, attachmentBlobUrls, getAttachmentUrl));
    lastGroup.dataset.search = (lastGroup.dataset.search || '') + ' ' + msgText(msg);
  }

  // Land on the most-recent message by default. rAF gives the browser a
  // frame to lay out before we scroll, so the height calculation is right.
  requestAnimationFrame(() => {
    container.scrollTop = container.scrollHeight;
    updateJumpButtons();
  });
}

function updateJumpButtons() {
  const c = $('browse-messages');
  if (!c) return;
  const nearBottom = c.scrollHeight - c.scrollTop - c.clientHeight < 100;
  $('btn-jump-top').hidden = false;                    // top button always visible
  $('btn-jump-bottom').hidden = nearBottom;            // hide "latest" when already at bottom
}

$('btn-jump-top').addEventListener('click', () => {
  const c = $('browse-messages');
  c.scrollTo({ top: 0, behavior: 'smooth' });
});
$('btn-jump-bottom').addEventListener('click', () => {
  const c = $('browse-messages');
  c.scrollTo({ top: c.scrollHeight, behavior: 'smooth' });
});
$('browse-messages').addEventListener('scroll', updateJumpButtons, { passive: true });

async function renderBrowseMessage(msg, attachmentBlobUrls, getAttachmentUrl) {
  if (msg.event?.kind === 'recording') return await renderRecording(msg, attachmentBlobUrls, getAttachmentUrl);
  if (msg.messageType === 'systemEventMessage' || msg.event) {
    const b = document.createElement('div'); b.className = 'msg system';
    b.textContent = msg.event ? `[${msg.event.kind}]` : (msg.body?.content ? stripTags(msg.body.content) : '[system event]');
    return b;
  }
  const wrap = document.createElement('div');
  const bubble = document.createElement('div');
  bubble.className = 'msg';
  if (msg.body?.contentType === 'html') {
    const html = rewriteInlinePaths(msg.body.content || '', attachmentBlobUrls);
    bubble.innerHTML = sanitizeHtml(html);
  } else {
    bubble.textContent = msg.body?.content || '';
  }
  if (msg.lastEditedDateTime) {
    const e = document.createElement('span'); e.className = 'edited'; e.textContent = '(edited)';
    bubble.appendChild(e);
  }
  wrap.appendChild(bubble);

  const files = (msg.attachments || []).filter((a) => a.name || a.localPath);
  if (files.length) {
    const box = document.createElement('div'); box.className = 'attachment-list';
    for (const att of files) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'attachment' + (att.localPath ? '' : ' missing');
      btn.innerHTML = `<span class="att-icon"></span><div class="att-info"><div class="att-name"></div><div class="att-type"></div></div>`;
      btn.querySelector('.att-icon').textContent = fileIcon(att.name || '');
      btn.querySelector('.att-name').textContent = att.name || '(unnamed)';
      btn.querySelector('.att-type').textContent = att.contentType || '';
      if (att.localPath) {
        btn.addEventListener('click', async () => {
          const url = await getAttachmentUrl(att.localPath);
          if (url) window.open(url, '_blank');
        });
      } else {
        btn.disabled = true;
      }
      box.appendChild(btn);
    }
    wrap.appendChild(box);
  }
  if (msg.reactions?.length) {
    const rx = document.createElement('div'); rx.className = 'reactions';
    for (const r of msg.reactions) {
      const chip = document.createElement('span'); chip.className = 'reaction';
      chip.textContent = `${emojiFor(r.reactionType)} ${r.user || ''}`.trim();
      rx.appendChild(chip);
    }
    wrap.appendChild(rx);
  }
  return wrap;
}

async function renderRecording(msg, attachmentBlobUrls, getAttachmentUrl) {
  const w = document.createElement('div'); w.className = 'recording';
  const t = document.createElement('div'); t.className = 'rec-title';
  t.textContent = '🎥 ' + (msg.event.displayName || 'Meeting recording');
  w.appendChild(t);
  if (msg.event.duration) {
    const m = document.createElement('div'); m.className = 'rec-meta'; m.textContent = `duration ${msg.event.duration}`;
    w.appendChild(m);
  }
  if (msg.event.localPath) {
    const url = await getAttachmentUrl(msg.event.localPath);
    if (url) {
      const v = document.createElement('video');
      v.controls = true; v.preload = 'metadata'; v.src = url;
      w.appendChild(v);
    }
  } else {
    const p = document.createElement('div'); p.className = 'rec-meta';
    p.textContent = 'Recording not downloaded (may require organizer access).';
    w.appendChild(p);
  }
  if (msg.event.transcriptLocalPath) {
    const a = document.createElement('a'); a.className = 'rec-transcript';
    a.href = '#'; a.textContent = '📝 Open transcript (VTT)';
    a.addEventListener('click', async (e) => {
      e.preventDefault();
      const url = await getAttachmentUrl(msg.event.transcriptLocalPath);
      if (url) window.open(url, '_blank');
    });
    w.appendChild(a);
  }
  return w;
}

function rewriteInlinePaths(html, attachmentBlobUrls) {
  return html.replace(/(src|href)="attachments\/([^"]+)"/gi, (_m, attr, name) => {
    const url = attachmentBlobUrls.get(name);
    return url ? `${attr}="${url}"` : `${attr}=""`;
  });
}

function sanitizeHtml(html) {
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  (function walk(node) {
    for (const el of [...node.children]) {
      const tag = el.tagName.toLowerCase();
      if (['script', 'style', 'iframe', 'object'].includes(tag)) { el.remove(); continue; }
      for (const attr of [...el.attributes]) {
        const n = attr.name.toLowerCase();
        if (n.startsWith('on')) el.removeAttribute(attr.name);
        if ((n === 'src' || n === 'href') && /^\s*(javascript|data):/i.test(attr.value)) el.removeAttribute(attr.name);
        if (n === 'src' && /^https?:/i.test(attr.value)) {
          el.removeAttribute('src');
          el.setAttribute('alt', '[remote image removed]');
        }
      }
      walk(el);
    }
  })(root);
  return root.innerHTML;
}

function stripTags(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
}
function msgText(msg) {
  if (!msg.body?.content) return '';
  if (msg.body.contentType === 'text') return msg.body.content;
  return stripTags(msg.body.content);
}
function fileIcon(name) {
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (['pdf'].includes(ext)) return '📕';
  if (['doc','docx'].includes(ext)) return '📘';
  if (['xls','xlsx','csv'].includes(ext)) return '📗';
  if (['ppt','pptx'].includes(ext)) return '📙';
  if (['png','jpg','jpeg','gif','webp','svg'].includes(ext)) return '🖼️';
  if (['mp4','mov','webm','mkv'].includes(ext)) return '🎬';
  if (['mp3','wav','m4a','ogg'].includes(ext)) return '🎵';
  if (['zip','tar','gz','7z','rar'].includes(ext)) return '🗜️';
  if (['vtt','srt'].includes(ext)) return '📝';
  return '📎';
}
function emojiFor(t) { return { like:'👍', heart:'❤️', laugh:'😄', surprised:'😮', sad:'😢', angry:'😠' }[t] || '👍'; }

$('msg-search').addEventListener('input', (e) => {
  const q = e.target.value.toLowerCase().trim();
  for (const g of document.querySelectorAll('#browse-messages .msg-group')) {
    const t = (g.dataset.search || '').toLowerCase();
    g.classList.toggle('hidden', !!q && !t.includes(q));
  }
});

// ---------- init ----------
(async () => {
  setStep('signin');
  try {
    const account = await state.auth.whoAmI();
    if (account) {
      setAccount({ username: account.username, name: account.name });
      await routePostSignIn();
    }
  } catch { /* not signed in yet */ }
})();
